import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Button from '#lib/components/button.jsx';
import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import XCircleIcon from '@heroicons/react/24/outline/esm/XCircleIcon';
import EyeIcon from '@heroicons/react/24/outline/esm/EyeIcon';
import { addClasses, mergeClasses } from '#lib/helpers/class.js';
import ShareIcon from '@heroicons/react/24/outline/esm/ShareIcon';
import { formatReadableDateAndHours } from '#helpers/helpers.js';
import { UserContext } from '#src/index.jsx';

export default function DropdownPublish(props) {
    const {
        publish,
        onRemovePublication,
        removePublication,
        publishedLink,
        busy,
        className,
        onOpen,
        lastPublish,
        handlePublish
    } = props;
    const { getLanguage } = useContext(UserContext);
    const lang = getLanguage();
    const [show, setShow] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShow(false);
            }
        }

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [menuRef]);

    return (
        <Menu as="div" className={mergeClasses('relative inline-block text-left w-full', className)}>
            <div className="w-full">
                <Menu.Button
                    ref={menuRef}
                    onClick={() => setShow(!show)}
                    className={
                        'inline-flex min-w-32 justify-center rounded-md bg-white px-3 py-[6px] text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                    }
                    disabled={busy}
                >
                    {!busy ? (
                        _t.crud.itinerary.publishActions()
                    ) : (
                        <Icon icon="svg-spinners:ring-resize" width="1.2rem" height="1.2rem" className="mr-1" />
                    )}
                    <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                </Menu.Button>
            </div>

            <Transition
                show={show}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items
                    className="absolute right-0 z-10 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    onClick={() => setShow(false)}
                >
                    <div className="py-1 ">
                        <Menu.Item className={addClasses('w-full')}>
                            <Button
                                size="xs"
                                disabled={publish.isLoading}
                                busy={publish.isLoading}
                                className="px-2 py-1 text-sm font-semibold relative overflow-hidden w-full text-neutral-700 hover:text-primary-700 flex flex-col items-start"
                                onClick={handlePublish}
                            >
                                <Icon
                                    icon="line-md:upload-outline-loop"
                                    width="1.2rem"
                                    height="1.2rem"
                                    className="mr-1"
                                />
                                {_t.crud.itinerary.publish()}
                            </Button>
                        </Menu.Item>
                        <Menu.Item className={addClasses('w-full')}>
                            <Button
                                onClick={() => {
                                    setShow(false);
                                    onRemovePublication();
                                }}
                                size="xs"
                                disabled={removePublication.isLoading}
                                busy={removePublication.isLoading}
                                destructive
                                className="px-2 py-1 text-sm font-semibold relative overflow-hidden w-full text-red-700 hover:text-red-600 flex flex-col items-start"
                            >
                                <XCircleIcon className="icon-sm mr-1" />
                                {_t.crud.itinerary.unpublish()}
                            </Button>
                        </Menu.Item>
                        <Menu.Item className={addClasses('w-full')}>
                            <Button
                                size="xs"
                                href={publishedLink}
                                target="_blank"
                                onClick={(e) => {
                                    setShow(false);
                                    e.stopPropagation();
                                }}
                                className="px-2 py-1 text-sm font-semibold w-full text-neutral-700 hover:text-primary-700 flex flex-col items-start"
                            >
                                <EyeIcon className="icon-sm mr-1" />
                                {_t.crud.itinerary.openItinerary()}
                            </Button>
                        </Menu.Item>

                        <Menu.Item className={addClasses('w-full')}>
                            <Button
                                onClick={() => {
                                    setShow(false);
                                    onOpen();
                                }}
                                size="xs"
                                className="px-2 py-1 text-sm font-semibold w-full text-neutral-700 hover:text-primary-700 flex flex-col items-start"
                            >
                                <ShareIcon width={18} height={18} className="mr-1" />
                                {_t.crud.itinerary.share()}
                            </Button>
                        </Menu.Item>
                        <div className="text-xs text-neutral-500 p-2 pb-1">
                            {`${_t.edit.fields.lastPublication()} ${formatReadableDateAndHours(
                                lastPublish,
                                lang
                            )}`}
                        </div>
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
}
